import { CheckCheck, EllipsisVertical, Flag, FlagOff, Move, PencilLine, SquarePen, Trash, Unlock } from "lucide-react";
import React, { useState } from "react";
import { Button } from "src/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { Invoice, InvoiceLineItem, SKU } from "src/redux/billing/billing.types";
import { isInvoiceLocked } from "../../utils/billing-utils";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/redux-store";
import { Company } from "../../types/billing.types";

interface InvoiceDrawerLineTableRowMenuProps {
  lineItem: InvoiceLineItem;
  invoice: Invoice;
  skus: Array<SKU>;
  invoices: Array<Invoice>;
  companies: Array<Company>;
  isLocked: boolean;
  updateLineItem: (lineItemId: string, field: string, value: any) => void;
  setNotes: (notes: string | null) => void;
}


const InvoiceDrawerLineTableRowMenu: React.FC<InvoiceDrawerLineTableRowMenuProps> = ({
  lineItem,
  invoice,
  skus,
  invoices,
  companies,
  isLocked,
  updateLineItem,
  setNotes,
}) => {
  const [selectedLineItem, setSelectedLineItem] = useState<InvoiceLineItem | null>(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [moveSearchQuery, setMoveSearchQuery] = useState('');
  const userState = useSelector((state: RootState) => state.userState);


  const filteredInvoices = invoices.filter(invoice => companies.find((company) => company.id === invoice.company)?.company_name.toLowerCase().includes(moveSearchQuery && moveSearchQuery.toLowerCase()));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          disabled={isLocked}
          size='sm'
          variant='ghost'
          className={`text-gray-600 text-sm ${isLocked ? '!cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <EllipsisVertical className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          onClick={() =>
            updateLineItem(
              lineItem.id,
              'invoice_line_item_status',
              lineItem.invoice_line_item_status === 'approved' ? 'draft' : 'approved'
            )}
        >
          {lineItem.invoice_line_item_status === 'approved' ? <PencilLine className='h-4 w-4' /> : <CheckCheck className='h-4 w-4' />}
          {lineItem.invoice_line_item_status === 'approved' ? 'Set to Draft' : 'Approve'}
        </DropdownMenuItem>
        {isInvoiceLocked(invoice, userState.permissions?.groups || []) && (
          <DropdownMenuItem
            onClick={() => {
              updateLineItem(lineItem.id, 'invoice_line_item_status', 'draft');
            }}
          >
            <Unlock className='h-4 w-4' />
            Unlock
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => {
            setNotes(' ');
          }}
        >
          <SquarePen className='h-4 w-4' />
          Note
        </DropdownMenuItem>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <DropdownMenuItem>
              <Move className='h-4 w-4' />
              Move
            </DropdownMenuItem>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align='start'
            side='left'
            className='w-fit !max-h-[300px] !overflow-y-auto z-[99999]'
          >
            <div>
              <input
                type="text"
                placeholder="Search companies"
                className="w-full text-sm h-8 border-b border-gray-400 text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
                value={moveSearchQuery}
                onChange={(e) => setMoveSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
            {filteredInvoices.map((invoice) => (
              <DropdownMenuItem
                key={invoice.id + lineItem.id}
                onClick={() => updateLineItem(lineItem.id, 'invoice', invoice.id)}
              >
                <div className='flex flex-col text-sm font-bold text-gray-700'>
                  {companies.find((company) => company.id === invoice.company)?.company_name}
                  <span className='text-xs font-light text-gray-500'>
                    {invoice.invoice_line_items.length}
                    {' '}
                    line item
                    {invoice.invoice_line_items.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenuItem
          onClick={() => updateLineItem(lineItem.id, 'attention_needed', !lineItem.attention_needed)}
        >
          {lineItem.attention_needed ? <FlagOff className='h-4 w-4' /> : <Flag className='h-4 w-4' />}
          {lineItem.attention_needed ? 'Resolve' : 'Flag'}
        </DropdownMenuItem>
        <DropdownMenuItem
          //   turn into a hard delete
          onClick={() => updateLineItem(lineItem.id, 'status', 'archived')}
        >
          <Trash className='h-4 w-4' />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default InvoiceDrawerLineTableRowMenu;